/**
 * Port helpers for CSML visual builder nodes.
 * Ported from builder4.html getOutPorts / getInPorts / nodeHasHold.
 */

export function getOutPorts(node) {
  if (
    (node.type === 'question' || node.type === 'start') &&
    node.buttons?.length
  ) {
    return node.buttons.map((b, i) => ({
      label: b.label || `btn${i}`,
      idx: i,
      type: 'button',
    }));
  }
  if (node.type === 'quickreply' && node.buttons) {
    return node.buttons.map((b, i) => ({
      label: b.label || `reply${i}`,
      idx: i,
      type: 'reply',
    }));
  }
  if (['radio', 'dropdown'].includes(node.type) && node.options) {
    return node.options.map((o, i) => ({
      label: o.label || `opt${i}`,
      idx: i,
      type: 'option',
    }));
  }
  // Multiselect and checkbox let the user tick several options and submit
  // once, so there is no single option to branch on - the generator can only
  // emit one unconditional goto after the hold. Showing a port per option
  // (as this used to) promised branches that silently became dead gotos.
  if (['multiselect', 'checkbox'].includes(node.type)) {
    return [
      { label: 'submitted', labelKey: 'SUBMITTED', idx: 0, type: 'default' },
    ];
  }
  if (node.type === 'carousel' && node.cards) {
    return node.cards
      .map((card, i) => {
        if (card.buttons && card.buttons.length > 0) {
          return card.buttons.map((b, j) => ({
            label: b.label || `card${i}btn${j}`,
            idx: i * 10 + j,
            type: 'cardbtn',
            cardIndex: i,
            buttonIndex: j,
          }));
        }
        return [
          {
            label: `card${i}`,
            idx: i,
            type: 'card',
            cardIndex: i,
            buttonIndex: null,
          },
        ];
      })
      .flat();
  }
  // Only working hours actually branches two ways. Calendar used to borrow
  // these same open/closed ports, which made no sense for a date picker and
  // left its second port permanently dead - the generator emits a single
  // goto after the hold whichever port you wire.
  if (node.type === 'workinghours') {
    return [
      { label: 'open', labelKey: 'OPEN', idx: 0, type: 'open' },
      { label: 'closed', labelKey: 'CLOSED', idx: 1, type: 'closed' },
    ];
  }
  if (node.type === 'calendar') {
    return [
      {
        label: 'date picked',
        labelKey: 'DATE_PICKED',
        idx: 0,
        type: 'default',
      },
    ];
  }
  if (node.type === 'router') {
    const ports = [
      { label: 'else', labelKey: 'FALLTHROUGH', idx: 0, type: 'default' },
    ];
    (node.keywords || []).forEach((rule, index) => {
      ports.push({
        label: rule.match || `match${index + 1}`,
        idx: index + 1,
        type: 'keyword',
      });
    });
    return ports;
  }
  if (node.type === 'api') {
    const ports = [
      { label: 'default', labelKey: 'DEFAULT', idx: 0, type: 'default' },
    ];
    if (node.responseRules && node.responseRules.length > 0) {
      node.responseRules.forEach((rule, i) => {
        ports.push({
          label: rule.label || `rule${i + 1}`,
          idx: i + 1,
          type: 'rule',
        });
      });
    }
    return ports;
  }
  return [{ label: 'next', labelKey: 'NEXT', idx: 0, type: 'default' }];
}

export function getInPorts() {
  return [{ label: 'in', labelKey: 'IN', idx: 0, type: 'default' }];
}

export function nodeHasHold(node) {
  return node.type === 'hold';
}

export function useCsmlPorts() {
  return { getOutPorts, getInPorts, nodeHasHold };
}
